import React, { useMemo, FC, useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import pluralize from 'pluralize';
import Pagination from '@atlaskit/pagination';
import { ProductDTO, TeamWithBreachesDTO } from '@atlassiansox/phobos-client-ts';
import { ExperienceSuccess as UFOExperienceSuccess, UFOExperience } from '@atlassian/ufo';

import { apiFetch } from '@/services';
import {
  useFetchErrorController,
  usePaginationController,
  filterStore,
  getSLOFilterQuery,
} from '@/controllers';
import { PrimaryType } from '@/meta';
import { mapParamsToAPIQuery } from '@/components/composed/catalog-search-bar/services/utils';
import { Params } from '@/components/composed/catalog-search-bar/types';
import {
  flexCenter,
  flexDirectionColumn,
  flexSpace,
  spaceGapCozy,
  spaceTopComfortable,
  spaceTopCozy,
  tableHeadStyles,
  tableRowsStyles,
  textTransformCapitalize,
} from '@/styles';
import { querySpec } from '@/services/api-service/api-queries';
import { typeToExperience } from '@/common/ufo/experiences';

import { getPagesFromTotalPage } from '../search/utils';
import * as stylesSearch from '../search/search-results/styles';
import SearchPageSkeleton from '../search/search-results/search-page-skeleton';
import * as styles from '../search/search-results/search-list-cards/styles';

import DashboardBasicListIteam from './dashboard-basic-list-item';
import * as stylesLocal from './styles';

type DashboardBasicListProps = {
  id: string;
  type: PrimaryType;
  experienceSeq: number;
};

/**
 * This is the component to display basic list on dashboard sub entity list tab
 */
const DashboardBasicList: FC<DashboardBasicListProps> = ({ id, type, experienceSeq }) => {
  const { onError } = useFetchErrorController();
  const { currentPage, onPageChange, setCurrentPage } = usePaginationController();
  const { state: filterState } = useContext(filterStore);
  const experience: UFOExperience | null = ['capability', 'product', 'team'].includes(type)
    ? typeToExperience(type as 'capability' | 'product' | 'team')
    : null;

  // fetch content
  const queryFilter = getSLOFilterQuery(filterState.slo);
  const queryParams = mapParamsToAPIQuery(
    { [type === 'team' ? 'product' : 'team']: [{ publicId: id }] } as Params,
    true
  );
  const { queryKey, path } = querySpec(`${type}.report.{params}.{page}`, {
    params: `${queryParams}${queryFilter}`,
    page: currentPage.toString(),
  });
  const { data, isInitialLoading: isLoading } = useQuery(queryKey, () => apiFetch(path), {
    onError,
  });

  const hasData = data?.content?.length > 0;
  const pages = useMemo(() => getPagesFromTotalPage(data?.totalPages), [data]);
  const showPagination = (data?.totalPages ?? 0) > 1;

  useEffect(() => {
    if (currentPage > 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFilter]);

  return (
    <div>
      {isLoading && (
        <div css={[styles.cardSkeletonStyles, styles.cardBasicSkeletonStyles]}>
          <SearchPageSkeleton isCompact />
        </div>
      )}
      {!isLoading && !hasData && (
        <div css={[flexCenter, flexDirectionColumn, spaceTopCozy, spaceGapCozy]}>
          <img src="/assets/not-found.svg" alt="not-found" />
          <p>No {pluralize(type)} found for the selected search</p>
        </div>
      )}
      {hasData && (
        <>
          <div
            css={[
              tableHeadStyles,
              flexSpace,
              spaceTopComfortable,
              stylesLocal.dashboardBasicListResultRowStyles,
              type === 'team' ? stylesLocal.dashboardBasicListTeamResultRowStyles : '',
            ]}
          >
            <div css={textTransformCapitalize}>{type} name</div>
            <div>Description</div>
            <div>SLOs Breaching</div>
            {type === 'product' && <div>w/ Quality Issue</div>}
            <div>Total SLOs</div>
          </div>
          <ul
            css={[
              spaceTopCozy,
              tableRowsStyles,
              stylesSearch.searchResultRowsStyles,
              showPagination ? '' : styles.cardBasicContentNoPaginationStyles,
            ]}
          >
            {data.content.map((entity: TeamWithBreachesDTO | ProductDTO) => (
              <DashboardBasicListIteam entity={entity} key={entity.publicId} type={type} />
            ))}
          </ul>
          {showPagination && (
            <div css={spaceTopCozy}>
              <Pagination selectedIndex={currentPage - 1} pages={pages} onChange={onPageChange} />
            </div>
          )}
          {experience && <UFOExperienceSuccess key={experienceSeq} experience={experience} />}
        </>
      )}
    </div>
  );
};

export default DashboardBasicList;
